/**
 * Submission handler — uploads auto-screenshot and user attachment, then either:
 * 1. posthog.capture("survey sent", ...) if PostHog is on the page
 * 2. Direct POST to /api/jira as fallback
 */

import type { ResolvedConfig, FeedbackMetadata } from "../types";

interface SubmitParams {
  config: ResolvedConfig;
  summary: string;
  text: string;
  feedbackType: string;
  contactEmail: string | null;
  jpdIdeaKey: string | null;
  jpdIdeaSummary: string | null;
  customerSegment: string | null;
  screenshotBlob: Blob | null;
  attachmentBlob: Blob | null;
  attachmentContentType: string | null;
  metadata: FeedbackMetadata;
}

interface SubmitResult {
  issueKey: string;
  issueUrl?: string;
}

export async function submitFeedback(params: SubmitParams): Promise<SubmitResult> {
  const {
    config,
    summary,
    text,
    feedbackType,
    contactEmail,
    jpdIdeaKey,
    jpdIdeaSummary,
    customerSegment,
    screenshotBlob,
    attachmentBlob,
    attachmentContentType,
    metadata,
  } = params;
  const apiUrl = config.apiUrl;

  // Step 1a: Upload auto-screenshot if captured
  let screenshotUrl: string | null = null;
  if (screenshotBlob && apiUrl) {
    try {
      const result = await uploadScreenshot(apiUrl, screenshotBlob);
      screenshotUrl = result;
    } catch (err) {
      console.warn("IPFeedback: Screenshot upload failed:", err);
    }
  }

  // Step 1b: Upload user attachment if provided
  let userAttachmentUrl: string | null = null;
  let userAttachmentContentType: string | null = null;
  if (attachmentBlob && apiUrl && attachmentContentType) {
    try {
      const result = await uploadAttachment(apiUrl, attachmentBlob, attachmentContentType);
      userAttachmentUrl = result.url;
      userAttachmentContentType = result.contentType;
    } catch (err) {
      console.warn("IPFeedback: Attachment upload failed:", err);
    }
  }

  // Step 2: Build event properties
  const properties: Record<string, unknown> = {
    $survey_id: config.surveyId || undefined,
    $survey_response: text,
    ip_summary: summary,
    ip_feedback_type: feedbackType,
    ip_contact_email: contactEmail,
    ip_screenshot_url: screenshotUrl,
    ip_user_attachment_url: userAttachmentUrl,
    ip_user_attachment_content_type: userAttachmentContentType,
    ip_jira_project: config.project,
    ip_jpd_project: config.jpdProject || null,
    ip_jpd_idea_key: jpdIdeaKey,
    ip_jpd_idea_summary: jpdIdeaSummary,
    ip_jpd_link_type: config.jpdLinkType,
    ip_customer_segment: customerSegment,
    ip_console_errors: JSON.stringify(metadata.consoleErrors),
    ip_network_errors: JSON.stringify(metadata.networkErrors),
    ip_viewport: `${metadata.viewportWidth}x${metadata.viewportHeight}@${metadata.devicePixelRatio}x`,
    ip_page_url: metadata.url,
    ip_page_title: metadata.title,
    ip_user_agent: metadata.userAgent,
    ip_page_load_ms: metadata.pageLoadMs,
    ip_time_on_page_ms: metadata.timeSinceLoadMs,
    ip_timestamp: metadata.timestamp,
    ip_timezone: metadata.timezone,
    // If PostHog isn't present globally, we may have collected these from cookie/localStorage.
    // Passing them through allows the webhook to render the PostHog section reliably.
    $session_replay_url: metadata.posthogSessionReplayUrl,
    $session_id: metadata.posthogSessionId,
    distinct_id: metadata.posthogDistinctId,
  };

  // Step 3: Send via PostHog or direct
  const ph = window.posthog;
  if (ph && config.surveyId) {
    ph.capture("survey sent", properties);
    return { issueKey: "Submitted" };
  } else {
    return await submitDirect(apiUrl, {
      project: config.project,
      jpdProject: config.jpdProject || null,
      jpdIdeaKey,
      jpdIdeaSummary,
      jpdLinkType: config.jpdLinkType,
      summary,
      text,
      feedbackType,
      contactEmail,
      customerSegment,
      screenshotUrl,
      userAttachmentUrl,
      userAttachmentContentType,
      pageUrl: metadata.url,
      userAgent: metadata.userAgent,
      viewport: `${metadata.viewportWidth}x${metadata.viewportHeight}@${metadata.devicePixelRatio}x`,
      sessionReplayUrl: metadata.posthogSessionReplayUrl,
      sessionId: metadata.posthogSessionId,
      distinctId: metadata.posthogDistinctId,
      consoleErrors: metadata.consoleErrors,
      networkErrors: metadata.networkErrors,
      pageLoadMs: metadata.pageLoadMs,
      timeOnPageMs: metadata.timeSinceLoadMs,
      timestamp: metadata.timestamp,
      timezone: metadata.timezone,
    });
  }
}

async function uploadScreenshot(apiUrl: string, blob: Blob): Promise<string> {
  const res = await fetch(`${apiUrl}/api/screenshot`, {
    method: "POST",
    headers: { "Content-Type": "image/jpeg" },
    body: blob,
  });

  if (!res.ok) {
    throw new Error(`Screenshot upload failed: ${res.status}`);
  }

  const data = await res.json();
  return data.url;
}

async function uploadAttachment(
  apiUrl: string,
  blob: Blob,
  contentType: string,
): Promise<{ url: string; contentType: string }> {
  // Step 1: Get a presigned S3 POST URL (bypasses API Gateway 10MB limit)
  const presignRes = await fetch(`${apiUrl}/api/presign?type=${encodeURIComponent(contentType)}`, {
    credentials: "omit",
  });
  if (!presignRes.ok) {
    throw new Error(`Presign request failed: ${presignRes.status}`);
  }
  const { url: postUrl, fields, fileUrl } = await presignRes.json();

  // Step 2: Upload directly to S3 via presigned POST
  const formData = new FormData();
  for (const [k, v] of Object.entries(fields as Record<string, string>)) {
    formData.append(k, v);
  }
  formData.append("file", blob);

  const uploadRes = await fetch(postUrl, { method: "POST", body: formData });
  if (!uploadRes.ok) {
    throw new Error(`S3 upload failed: ${uploadRes.status}`);
  }

  return { url: fileUrl, contentType };
}

async function submitDirect(
  apiUrl: string,
  payload: Record<string, unknown>,
): Promise<SubmitResult> {
  const res = await fetch(`${apiUrl}/api/jira`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });

  if (!res.ok) {
    throw new Error(`Submission failed: ${res.status}`);
  }

  const data = await res.json();
  return {
    issueKey: data.issueKey || "Submitted",
    issueUrl: data.issueUrl,
  };
}
